<i18n
  src="@/locales/views/organization/partners/producers/edit_crop_field.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/crop_field.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    name="org_partners_producers_show_producer_path"
    :params="{
      organization_id: organization_id,
      producer_id: producer_id
    }"
  />
  <bread-crumbs
    :labels="[module_name, producer?.organization_producer?.producer_code]"
  ></bread-crumbs>

  <alert-danger :errors_data="std_err" />

  <template v-if="load_general_data">
    <producer-info :producer="producer" :organization_id="organization_id" />

    <div class="mt-8">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body">
          <div class="space-y-8 divide-y divide-gray-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{
                    $t(
                      "model.crop_field.organization_crop_field.crop_field_code"
                    )
                  }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="
                      crop_field.organization_crop_field.crop_field_code
                    "
                    :class="[
                      'input-text',
                      isInvalid('organization_crop_field.crop_field_code')
                    ]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.name") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.name"
                    :class="['input-text', isInvalid('name')]"
                  />
                </div>
              </div>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.hectares") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.hectares"
                    :class="['input-text', isInvalid('hectares')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.square_meters") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.square_meters"
                    :class="['input-text', isInvalid('square_meters')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.crop_field_type_id") }}
                </label>
                <div class="mt-1">
                  <radio-form
                    v-model="crop_field.crop_field_type_id"
                    name="crop_field_type_id"
                    :options="cropFieldTypes"
                  />
                </div>
              </div>
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" v-if="crop_field.crop_field_type_id == 1">
              
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('planting_date')]">
                  {{ $t("model.crop_field.planting_date") }}
                </label>
                <div class="mt-1">
                  <date-form v-model="crop_field.planting_date" mode="month" />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.harvest_time") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.harvest_time"
                    :class="['input-text', isInvalid('harvest_time')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('crop')]">
                  {{ $t("model.crop_field.crop_id") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crops"
                    v-model="crop_field.crop_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('crop_variety')]">
                  {{ $t("model.crop_field.crop_variety_id") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crop_varieties"
                    v-model="crop_field.crop_variety_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop_variety')"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label :class="['label-form', isInvalidLabel('tree_number')]">
                  {{ $t("model.crop_field.tree_number") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.tree_number"
                    :class="['input-text', isInvalid('tree_number')]"
                  />
                </div>
              </div>
              
            </div>
            <div class="pt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.organization_crop_field.crop_field_location") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crop_field_locations"
                    :clearable="false"
                    v-model="crop_field_location_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop_field_loction')"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.organization_crop_field.crop_field_zone") }}
                </label>
                <div class="mt-1">
                  <select-form
                    :options="crop_field_zones"
                    :clearable="false"
                    v-model="crop_field.organization_crop_field.crop_field_zone_id"
                    label="name"
                    :is_invalid="isInvalidSelect('crop_field_zone')"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.longitude_dd") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.longitude_dd"
                    :class="['input-text', isInvalid('longitude_dd')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.latitude_dd") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.latitude_dd"
                    :class="['input-text', isInvalid('latitude_dd')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.longitude_dms") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.longitude_dms"
                    :class="['input-text', isInvalid('longitude_dms')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.latitude_dms") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.latitude_dms"
                    :class="['input-text', isInvalid('latitude_dms')]"
                  />
                </div>
              </div>
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.crop_field.altitude") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="crop_field.altitude"
                    :class="['input-text', isInvalid('altitude')]"
                  />
                </div>
              </div>
              
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <div class="panel-footer-actions">
            <white-button
              @click.prevent="goToShowProducer()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!proc_save_button"
              @click.prevent="update()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
  </template>

  <loading-message v-else />
</template>

<script>
import Requester from "@/views/organization/requesters/partners/producers/manage_crop_field.js";

import CropFieldTypesMixin from "@/components/views/mixins/organization/partners/producers/crop_field_types.js";

import RequesterManageProducer from "@/views/organization/requesters/partners/producers/manage_producer.js";
import ProducerInfo from "@/components/views/organization/partners/producers/ProducerInfo.vue";

export default {
  name: "OrgPartnersProducersEditCropField",
  mixins: [CropFieldTypesMixin],
  components: { ProducerInfo },
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      producer: {},
      crop_field: {},
      crops: [],
      crop_varieties: [],
      crop_field_location_id: null,
      crop_field_locations: []
    };
  },
  props: {
    organization_id: {},
    producer_id: {},
    crop_field_id: {}
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id
        }
      }).then(() => this.loadData())
    );
  },
  computed: {
    crop_field_zones: function () {
      if (this.crop_field_location_id) {
        let location = this.crop_field_locations.find(location => location.id == this.crop_field_location_id);
        return location?.crop_field_zones || [];
      }
      return [];
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchProducer();
      await this.fetchCrops();
      await this.fetchCropFieldLocations();
      await this.fetchCropField();
      this.load_general_data = true;
    },
    fetchProducer: async function () {
      let response = await RequesterManageProducer.fetchProducer(
        this.organization_id,
        this.producer_id
      );

      this.producer = response.data;
    },
    fetchCrops: async function () {
      let response = await Requester.fetchCrops(this.organization_id);

      this.crops = response.data;
    },
    fetchCropFieldLocations: async function () {
      let response = await Requester.fetchCropFieldLocations(this.organization_id);

      this.crop_field_locations = response.data;
    },
    fetchCropField: async function () {
      let response = await Requester.fetchCropField(
        this.organization_id,
        this.crop_field_id
      );

      this.crop_field_location_id = response.data.organization_crop_field.crop_field_zone.crop_field_location_id;
      let crop_field_zone_id = response.data.organization_crop_field.crop_field_zone_id;
      this.crop_field = response.data;
      this.crop_field.organization_crop_field.crop_field_zone_id = crop_field_zone_id;
    },
    update: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.updateCropField(
          this.organization_id,
          this.crop_field_id,
          this.crop_field
        );

        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowProducer();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
        this.proc_save_button = false;
      }
    },
    goToShowProducer: function () {
      this.$router.push({
        name: "org_partners_producers_show_producer_path",
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id
        }
      });
    }
  },
  watch: {
    crop_field: {
      deep: true,
      handler(new_value) {
        this.crop_varieties =
          this.crops.find(crop => crop.id == new_value.crop_id)
            ?.crop_varieties || [];

        if (
          !this.crop_varieties
            .map(cv => cv.id)
            .includes(this.crop_field.crop_variety_id)
        )
          this.crop_field.crop_variety_id = null;
      }
    },
    crop_field_locations: function (crop_field_locations) {
      if (crop_field_locations.length == 1) {
        this.crop_field_location_id = crop_field_locations[0].id;
      }
    },
    crop_field_zones: function (crop_field_zones) {
      if (this.crop_field && crop_field_zones.lenght == 1) {
        this.crop_field.organization_crop_field.crop_field_zone_id = crop_field_zones[0].id;
      }
    },
    crop_field_location_id: function (current, prev) {
      current;
      if (this.crop_field && prev)
        this.crop_field.organization_crop_field.crop_field_zone_id = null;
    }
  }
};
</script>
